import { useState } from 'react';
import './InventoryGrid.css';
import './ZoneTheme.css';

// The sticker strip from the Inventory cards, lifted out so every other place
// that shows an item can wear the same one: five slots in applied order, the
// picture in each, and a hover card with the sticker's name, price and wear.
//
// The charm rides on the end of the same strip rather than in a component of
// its own. It is the same object on screen — a small square with a picture and
// a hover card — and it is what CS2 and every market site draw beside the
// stickers, so a second component would have been the same code, one call site
// further from the thing it belongs to.
//
// The two skins are the two the app already had — Inventory's dark tiles
// (.phi-sticker-*) and the Zone board's hard-edged boxes (.zn-inv-*). Nothing
// here is new CSS; the markup is identical to what those cards were rendering
// inline, so a sticker row looks the same wherever it turns up.
//
// Clicking a sticker copies its name, which is how you paste it into a market
// search. The click is stopped from travelling, so a strip sitting on a card
// that itself opens an editor or ticks a checkbox stays harmless.

const SKINS = {
  classic: {
    row: 'phi-sticker-row', slot: 'phi-sticker-slot', copied: 'phi-sticker-slot--copied',
    num: 'phi-sticker-slot-num', tip: 'phi-sticker-tooltip', tipCopied: 'phi-sticker-tooltip-copied',
    name: 'phi-tooltip-name', price: 'phi-tooltip-price', hint: 'phi-tooltip-hint',
    charm: 'phi-charm-slot',
  },
  zone: {
    row: 'zn-inv-stickers', slot: 'zn-inv-slot', copied: 'copied',
    num: 'zn-inv-slot-num', tip: 'zn-inv-tip', tipCopied: 'copied',
    name: 'name', price: 'price', hint: 'hint',
    charm: 'zn-inv-charm',
  },
};

// "Charm | Hot Howl" -> "Hot Howl". The stored name is the market one, because
// that is the key it is priced and searched under; the strip has one square to
// say it in, so the part that identifies it is what gets drawn.
const charmLabel = name => String(name || '').replace(/^(?:Souvenir\s+)?Charm\s*\|\s*/i, '');

/**
 * @param {Array}  stickers  [{ name, image, wear, slot }]
 * @param {Array}  charms    [{ name, image, pattern, slot }] — names already in
 *        market form ("Charm | Backsplash"), so they are their own price key.
 *        Drawn after the sticker slots, and only when there is one: an item
 *        with no charm gets no empty square, because unlike the five sticker
 *        slots there is no fixed number of them to hold open.
 * @param {Object} prices    live price map, read as `Sticker | <name>`
 * @param {'classic'|'zone'} variant
 * @param {number} max       slots to draw (CS2 allows 5)
 * @param {boolean} hideWhenEmpty  render nothing for an item with no stickers.
 *        True inside modals, where the strip is one section among many and an
 *        empty one is just noise. False on card grids, where every card has to
 *        keep the same shape — see `blank`.
 * @param {boolean} blank  reserve the row's space but draw nothing in it. Cards
 *        are laid out as one template, so an item that cannot take stickers at
 *        all (a case, an agent) still holds the row open and stays the same
 *        height as the crafts beside it.
 * @param {number} maxWidth  caps the strip in wide containers, so slots in a
 *        modal stay sticker-sized instead of stretching to the panel.
 */
export default function StickerSlots({
  stickers, charms, prices, variant = 'classic', max = 5,
  hideWhenEmpty = true, blank = false, maxWidth, className = '', style,
}) {
  const [hovered, setHovered] = useState(null);
  const [copied, setCopied]   = useState(null);

  const applied = (stickers || []).filter(s => s?.name);
  const fitted  = blank ? [] : (charms || []).filter(c => c?.name);
  if (hideWhenEmpty && applied.length === 0 && fitted.length === 0) return null;

  const c    = SKINS[variant] || SKINS.classic;
  const list = blank ? [] : (stickers || []);

  // A charm on an item wearing no stickers draws the charm alone. The five
  // empty boxes are a TEMPLATE — they exist so cards in a grid line up — and a
  // caller that asked to hide an empty strip has already said it does not want
  // that template. Without this, a charmed-but-stickerless sold ticket showed
  // five blanks it had just been told not to show.
  const count = (hideWhenEmpty && applied.length === 0) ? 0 : max;
  const slots = Array.from({ length: count }, (_, i) => list[i] || null);

  const copy = (sticker, i) => {
    navigator.clipboard?.writeText(sticker.name || '').then(() => {
      setCopied(i);
      setTimeout(() => setCopied(null), 1400);
    }).catch(() => {});
  };

  return (
    <div className={`${c.row}${className ? ` ${className}` : ''}`}
      style={{
        ...(maxWidth ? { maxWidth } : null),
        ...(blank ? { visibility: 'hidden', pointerEvents: 'none' } : null),
        ...style,
      }}>
      {slots.map((sticker, i) => {
        const price = sticker?.name ? prices?.[`Sticker | ${sticker.name}`]?.starting_at?.price : null;
        const wear  = sticker?.wear != null ? Math.round(sticker.wear * 100) : null;
        const isCopied = copied === i;
        return (
          <div
            key={i}
            className={`${c.slot}${sticker ? ' filled' : ''}${isCopied ? ` ${c.copied}` : ''}`}
            onMouseEnter={() => sticker && setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            onClick={sticker ? e => { e.stopPropagation(); copy(sticker, i); } : undefined}
            onMouseDown={sticker ? e => e.stopPropagation() : undefined}
          >
            <span className={c.num}>{i + 1}</span>
            {sticker?.image && <img src={sticker.image} alt="" loading="lazy" />}
            {sticker && (hovered === i || isCopied) && (
              <div className={`${c.tip}${isCopied ? ` ${c.tipCopied}` : ''}`} style={{ opacity: 1 }}>
                {isCopied ? (
                  <span className={variant === 'zone' ? 'copied-text' : c.tipCopied}>Copied!</span>
                ) : (
                  <>
                    <span className={c.name}>{sticker.name}</span>
                    {price != null && <span className={c.price}>${price.toFixed(2)}</span>}
                    {wear != null && wear > 0 && <span className={c.hint}>{wear}% worn</span>}
                    <span className={c.hint}>Click to copy</span>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* The charm, on the end of the same rail. */}
      {fitted.map((charm, i) => (
        <CharmSlot key={`charm-${i}`} charm={charm} prices={prices} variant={variant} />
      ))}

    </div>
  );
}

/**
 * One charm, drawn as a slot on the end of a sticker rail.
 *
 * Its own component with its own hover state because it has three homes: this
 * strip, and the two inventory grids (SkinTable's classic card and
 * InventoryTabZone's) whose sticker rows predate StickerSlots and are still
 * written inline. Those two can drop a charm on the end of what they already
 * render instead of growing a third copy of this markup.
 *
 * Priced off `charm.name` directly — the name is stored in market form
 * ("Charm | Backsplash"), so unlike a sticker it needs no prefix to become a
 * price key.
 */
export function CharmSlot({ charm, prices, variant = 'classic' }) {
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied]   = useState(false);
  if (!charm?.name) return null;

  const c     = SKINS[variant] || SKINS.classic;
  const price = prices?.[charm.name]?.starting_at?.price;

  const copy = e => {
    e.stopPropagation();
    navigator.clipboard?.writeText(charm.name).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  };

  return (
    <div
      className={`${c.slot} ${c.charm} filled${copied ? ` ${c.copied}` : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={copy}
      onMouseDown={e => e.stopPropagation()}
    >
      {charm.image
        ? <img src={charm.image} alt="" loading="lazy" />
        : <span className={c.num}>C</span>}
      {(hovered || copied) && (
        <div className={`${c.tip}${copied ? ` ${c.tipCopied}` : ''}`} style={{ opacity: 1 }}>
          {copied ? (
            <span className={variant === 'zone' ? 'copied-text' : c.tipCopied}>Copied!</span>
          ) : (
            <>
              <span className={c.name}>{charmLabel(charm.name)}</span>
              {price != null && <span className={c.price}>${price.toFixed(2)}</span>}
              {/* Pattern is what a rare charm is actually priced on. Only
                  CSFloat reports it; a Steam sync leaves it null. */}
              <span className={c.hint}>
                charm{charm.pattern != null ? ` · pattern ${charm.pattern}` : ''}
              </span>
              <span className={c.hint}>Click to copy</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
